import { z } from "zod";

/**
 * A sandbox class is a named set of resource limits a workload runs under.
 * It is the "where it runs" half of the sandbox/workload split: an agent
 * profile (the "what runs" half) points at a class instead of carrying its
 * own limits, so many workloads can share one sized box and the console has
 * one place to edit it. The provider (nsjail/local/embedded) is still a host
 * property; a class only sizes the box, it does not choose the isolation.
 */
export const sandboxClassLimitsSchema = z.object({
	maxCpus: z.number().int().min(1).max(64),
	maxMemMb: z.number().int().min(32).max(65536),
	// Scratch-space cap (the jail's writable tmpfs mounts today, a microVM's
	// rootfs.size later), in MiB.
	maxDiskMb: z.number().int().min(16).max(65536).default(2048),
	// Boundary details the console no longer asks about: defaults here let a
	// create omit them; they stay patchable through the API.
	maxPids: z.number().int().min(1).max(4096).default(64),
	maxFsizeMb: z.number().int().min(1).max(65536).default(256),
	maxTimeSec: z.number().int().min(1).max(86400),
});

export type SandboxClassLimits = z.infer<typeof sandboxClassLimitsSchema>;

export const sandboxClassSchema = sandboxClassLimitsSchema.extend({
	name: z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/, "Lowercase letters, digits, and dashes only"),
	description: z.string().max(500).default(""),
});

export type SandboxClassInput = z.infer<typeof sandboxClassSchema>;
